import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { withoutProposalWorks } from "../lib/proposalExclusion";

const MAX_FILE_SIZE = 15 * 1024 * 1024;
const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
];

const normalizeImages = (images) => {
  if (!images) return [];
  if (Array.isArray(images)) return images;
  try {
    const parsed = typeof images === "string" ? JSON.parse(images) : images;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getArtworkImage = (artwork) => {
  const images = normalizeImages(artwork?.images);
  const primary = images.find((img) => img?.isPrimary) || images[0];
  return primary?.url || artwork?.image_url || "";
};

export default function AuthenticityRequestPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [artworks, setArtworks] = useState([]);
  const [artworkId, setArtworkId] = useState(searchParams.get("artwork") || "");
  const [provenanceNotes, setProvenanceNotes] = useState("");
  const [requestNotes, setRequestNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [declaration, setDeclaration] = useState(false);

  const [loadingWorks, setLoadingWorks] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const selectedArtwork = useMemo(
    () => artworks.find((work) => work.id === artworkId) || null,
    [artworks, artworkId]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadArtworks() {
      if (!user?.id) return;
      setLoadingWorks(true);
      setError("");

      const { data, error: worksError } = await withoutProposalWorks(supabase
        .from("artworks")
        .select(
          "id,title,artist,year,inventory_number,image_url,images,certificate_auth,certificate_authenticity"
        )
        .eq("client_id", user.id)
        .order("artist", { ascending: true }), user.id);

      if (cancelled) return;

      if (worksError) {
        console.error("[AuthenticityRequest] artworks load", worksError);
        setError(t("authenticityRequest.errors.artworks"));
        setArtworks([]);
      } else {
        setArtworks(data || []);
        if (data?.length === 1) setArtworkId((prev) => prev || data[0].id);
      }
      setLoadingWorks(false);
    }

    loadArtworks();
    return () => {
      cancelled = true;
    };
  }, [user?.id, t]);

  const handleFiles = (event) => {
    const chosen = Array.from(event.target.files || []);
    const valid = [];

    for (const file of chosen) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(t("authenticityRequest.errors.fileType"));
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(t("authenticityRequest.errors.fileSize"));
        continue;
      }
      valid.push(file);
    }

    setFiles((previous) => [...previous, ...valid].slice(0, 12));
    event.target.value = "";
  };

  const removeFile = (index) => {
    setFiles((previous) => previous.filter((_, i) => i !== index));
  };

  const uploadAttachments = async (requestId) => {
    const attachments = [];

    for (const file of files) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
      const path = `${user.id}/${requestId}/${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("authenticity-requests")
        .upload(path, file, {
          upsert: false,
          contentType: file.type,
        });

      if (uploadError) throw uploadError;

      attachments.push({
        path,
        name: file.name,
        type: file.type,
        size: file.size,
      });
    }

    return attachments;
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    if (!user?.id || !artworkId) {
      setError(t("authenticityRequest.errors.artworkRequired"));
      return;
    }

    if (!declaration) {
      setError(t("authenticityRequest.errors.declarationRequired"));
      return;
    }

    setSubmitting(true);
    const requestId = crypto.randomUUID();

    try {
      const attachments = await uploadAttachments(requestId);

      const { error: insertError } = await supabase
        .from("authenticity_requests")
        .insert({
          id: requestId,
          user_id: user.id,
          artwork_id: artworkId,
          status: "submitted",
          provenance_notes: provenanceNotes.trim() || null,
          request_notes: requestNotes.trim() || null,
          attachments,
        });

      if (insertError) throw insertError;

      navigate(`/authenticity-request/${requestId}/success`, {
        replace: true,
        state: {
          artworkTitle: selectedArtwork?.title,
          artist: selectedArtwork?.artist,
        },
      });
    } catch (submitError) {
      console.error("[AuthenticityRequest] submit", submitError);
      setError(t("authenticityRequest.errors.submit"));
    } finally {
      setSubmitting(false);
    }
  };

  const image = selectedArtwork ? getArtworkImage(selectedArtwork) : "";

  return (
    <main className="min-h-screen bg-[#f7f7f5] px-4 py-8 md:px-8">
      <div className="mx-auto max-w-4xl">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-6 text-sm text-gray-600 hover:text-black"
        >
          ← {t("common.back", { defaultValue: "Retour" })}
        </button>

        <div className="rounded-2xl border border-gray-200 bg-white p-6 md:p-10">
          <div className="max-w-2xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-[#9b8350]">
              {t("authenticityRequest.eyebrow")}
            </p>
            <h1
              className="text-3xl font-light text-gray-950 md:text-4xl"
              style={{ fontFamily: "Georgia, serif" }}
            >
              {t("authenticityRequest.title")}
            </h1>
            <p className="mt-4 text-sm leading-6 text-gray-600">
              {t("authenticityRequest.intro")}
            </p>

            <div className="mt-5 rounded-xl border border-[#e6d9b3] bg-[#faf6ec] p-4 text-sm leading-6 text-[#6b5b39]">
              {t("authenticityRequest.notice")}
            </div>
          </div>

          <form onSubmit={submit} className="mt-10 space-y-8">
            <section>
              <label className="mb-2 block text-sm font-semibold text-gray-900">
                {t("authenticityRequest.artwork.label")}
              </label>

              {loadingWorks ? (
                <div className="rounded-xl border border-gray-200 p-4 text-sm text-gray-500">
                  {t("common.loading.safe")}
                </div>
              ) : artworks.length === 0 ? (
                <div className="rounded-xl border border-gray-200 p-5">
                  <p className="text-sm text-gray-600">
                    {t("authenticityRequest.artwork.empty")}
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate("/add-artwork")}
                    className="mt-3 rounded-lg bg-black px-4 py-2 text-sm font-medium text-white"
                  >
                    {t("authenticityRequest.artwork.add")}
                  </button>
                </div>
              ) : (
                <>
                  <select
                    value={artworkId}
                    onChange={(e) => setArtworkId(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-black"
                  >
                    <option value="">{t("authenticityRequest.artwork.placeholder")}</option>
                    {artworks.map((work) => (
                      <option key={work.id} value={work.id}>
                        {[work.artist, work.title, work.year].filter(Boolean).join(" — ")}
                      </option>
                    ))}
                  </select>

                  {selectedArtwork && (
                    <div className="mt-4 flex items-center gap-4 rounded-xl border border-gray-200 p-4">
                      {image ? (
                        <img
                          src={image}
                          alt=""
                          className="h-20 w-20 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="h-20 w-20 rounded-lg bg-gray-100" />
                      )}
                      <div>
                        <div className="font-medium text-gray-950">
                          {selectedArtwork.artist || t("common.unknown", { defaultValue: "Artiste inconnu" })}
                        </div>
                        <div className="text-sm text-gray-600">
                          {selectedArtwork.title}
                          {selectedArtwork.year ? `, ${selectedArtwork.year}` : ""}
                        </div>
                        {selectedArtwork.certificate_auth && (
                          <div className="mt-1 text-xs text-amber-700">
                            {t("authenticityRequest.artwork.existingCertificate")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>

            <section>
              <label className="mb-2 block text-sm font-semibold text-gray-900">
                {t("authenticityRequest.provenance.label")}
              </label>
              <p className="mb-3 text-xs leading-5 text-gray-500">
                {t("authenticityRequest.provenance.help")}
              </p>
              <textarea
                value={provenanceNotes}
                onChange={(e) => setProvenanceNotes(e.target.value)}
                rows={5}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-black"
                placeholder={t("authenticityRequest.provenance.placeholder")}
              />
            </section>

            <section>
              <label className="mb-2 block text-sm font-semibold text-gray-900">
                {t("authenticityRequest.documents.label")}
              </label>
              <p className="mb-3 text-xs leading-5 text-gray-500">
                {t("authenticityRequest.documents.help")}
              </p>

              <label className="flex min-h-28 cursor-pointer items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 px-5 py-6 text-center hover:bg-gray-100">
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    {t("authenticityRequest.documents.cta")}
                  </span>
                  <span className="mt-1 block text-xs text-gray-500">
                    PDF, JPG, PNG, WebP · 15 MB max · 12 fichiers max
                  </span>
                </span>
                <input
                  type="file"
                  multiple
                  accept=".pdf,image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleFiles}
                />
              </label>

              {files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {files.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2"
                    >
                      <span className="truncate pr-3 text-sm text-gray-700">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-xs text-gray-500 hover:text-red-700"
                      >
                        {t("common.remove", { defaultValue: "Retirer" })}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <label className="mb-2 block text-sm font-semibold text-gray-900">
                {t("authenticityRequest.notes.label")}
              </label>
              <textarea
                value={requestNotes}
                onChange={(e) => setRequestNotes(e.target.value)}
                rows={4}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-black"
                placeholder={t("authenticityRequest.notes.placeholder")}
              />
            </section>

            <label className="flex items-start gap-3 rounded-xl border border-gray-200 p-4">
              <input
                type="checkbox"
                checked={declaration}
                onChange={(e) => setDeclaration(e.target.checked)}
                className="mt-1"
              />
              <span className="text-xs leading-5 text-gray-600">
                {t("authenticityRequest.declaration")}
              </span>
            </label>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="rounded-xl border border-gray-300 px-5 py-3 text-sm font-medium"
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                disabled={submitting || loadingWorks || !artworkId}
                className="rounded-xl bg-black px-6 py-3 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting
                  ? t("authenticityRequest.submitting")
                  : t("authenticityRequest.submit")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}
